import { useQuery } from '@tanstack/react-query';
import { isSelectResult, runQuery } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';
import { isUnionScope } from '@/lib/useGraphScope';

const DCT = 'http://purl.org/dc/terms/';

/** The statements that describe an ontology, in the order the section shows them. A
 *  declaration carrying none of the `describes` fields is a file's stamp (a name and a
 *  version) and is not shown: the section that listed every such header was removed as
 *  junk (6.9), and comes back showing only what describes an ontology (6.10). */
export const ONTOLOGY_FIELDS = [
  { key: 'label', name: 'Title', predicate: ':label', many: false, long: false, describes: false },
  { key: 'definition', name: 'Definition', predicate: ':definition', many: false, long: true, describes: true },
  { key: 'description', name: 'Description', predicate: ':description', many: false, long: true, describes: true },
  { key: 'versionInfo', name: 'Version', predicate: ':versionInfo', many: false, long: false, describes: false },
  { key: 'creator', name: 'Creators', predicate: `${DCT}creator`, many: true, long: false, describes: true },
  { key: 'contributor', name: 'Contributors', predicate: `${DCT}contributor`, many: true, long: false, describes: true },
  { key: 'created', name: 'Created', predicate: `${DCT}created`, many: false, long: false, describes: true },
  { key: 'modified', name: 'Modified', predicate: `${DCT}modified`, many: false, long: false, describes: true },
  { key: 'license', name: 'License', predicate: `${DCT}license`, many: false, long: false, describes: true },
  { key: 'rights', name: 'Rights / copyright', predicate: `${DCT}rights`, many: false, long: true, describes: true },
  { key: 'comment', name: 'Comments', predicate: ':comment', many: true, long: true, describes: true },
] as const;

type FieldKey = (typeof ONTOLOGY_FIELDS)[number]['key'];

export interface OntologyDescription {
  iri: string;
  values: Partial<Record<FieldKey, { predicate: string; value: string }[]>>;
}

export function localName(uri: string): string {
  const i = Math.max(uri.lastIndexOf('#'), uri.lastIndexOf('/'), uri.lastIndexOf(':'));
  return i >= 0 ? uri.slice(i + 1) : uri;
}

/** The declarations in scope that describe an ontology; a bare file header is left out. */
export function describedOntologies(
  rows: { s: string; p: string; v: string }[],
): OntologyDescription[] {
  const byIri = new Map<string, OntologyDescription>();
  for (const { s, p, v } of rows) {
    const key = localName(p) === 'title' ? 'label' : localName(p);
    const field = ONTOLOGY_FIELDS.find((f) => f.key === key);
    if (!field) continue;
    const d = byIri.get(s) ?? { iri: s, values: {} };
    (d.values[field.key] ??= []).push({ predicate: p, value: v });
    byIri.set(s, d);
  }
  return [...byIri.values()].filter((d) =>
    ONTOLOGY_FIELDS.some((f) => f.describes && (d.values[f.key]?.length ?? 0) > 0),
  );
}

export function useDescribedOntologies(graphs: string[]) {
  const { token, ready } = useApiSession();
  const scope = isUnionScope(graphs) ? [] : graphs;
  return useQuery({
    queryKey: ['ontology-about', [...scope].sort().join('\u0000')],
    enabled: ready && graphs.length > 0,
    queryFn: async () => {
      const res = await runQuery(
        {
          query: 'FIND ?s ?p ?v WHERE { ?s :type :Ontology . ?s ?p ?v }',
          ...(scope.length > 0 ? { graphs: scope } : {}),
        },
        token,
      );
      if (!res.ok) throw new Error(res.message);
      const rows = isSelectResult(res.data)
        ? res.data.results.bindings.flatMap((b) =>
            b.s?.value && b.p?.value && b.v?.value !== undefined
              ? [{ s: b.s.value, p: b.p.value, v: b.v.value }]
              : [],
          )
        : [];
      return describedOntologies(rows);
    },
  });
}
